/**
 * StatusChip - Shared Status Indicator Component for All ML Infrastructure Pages
 * 
 * Provides consistent status chip with color coding.
 * 
 * Following BRAIN-PRE-CODE-CHECKLIST.md:
 * - Clean single imports
 * - Consistent color mapping
 */

import React from 'react';
import { Chip } from '@mui/material';

export type StatusType = 
  | 'success' 
  | 'error' 
  | 'warning' 
  | 'info' 
  | 'default'
  | 'production'
  | 'staging'
  | 'pending'
  | 'deprecated'
  | 'failed'
  | 'running'
  | 'completed';

interface StatusChipProps {
  status: StatusType | string;
  label?: string;
  size?: 'small' | 'medium';
}

const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  const statusLower = status.toLowerCase();
  
  if (['success', 'completed', 'production', 'healthy'].includes(statusLower)) {
    return 'success';
  }
  if (['error', 'failed', 'down', 'critical'].includes(statusLower)) {
    return 'error';
  }
  if (['warning', 'degraded', 'deprecated', 'needs-investigation'].includes(statusLower)) {
    return 'warning';
  }
  if (['info', 'running', 'in-progress', 'staging'].includes(statusLower)) {
    return 'info';
  }
  return 'default';
};

export const StatusChip: React.FC<StatusChipProps> = ({
  status,
  label,
  size = 'small'
}) => {
  const displayLabel = label || status.replace(/-/g, ' ').replace(/_/g, ' ');
  const color = getStatusColor(status);

  return (
    <Chip
      label={displayLabel}
      color={color}
      size={size}
      sx={{
        textTransform: 'capitalize',
        fontWeight: 500
      }}
    />
  );
};

export default StatusChip;
